/**
 * The three standard states every data-bearing screen must implement (docs/06 §4):
 * Loading (skeletons that match the final layout — never a bare spinner), Empty (icon + one
 * sentence + the action that fixes it), Error (inline panel with a retry button).
 */
import { AlertCircle, Inbox, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Skeleton, cn } from '@most/ui';
import type { ReactNode } from 'react';

/**
 * Skeleton rows shaped like the table that is coming, so the layout does not jump.
 *
 * The widths are varied rather than uniform. A grid of identical grey bars reads as a loading
 * *pattern*; ragged ones read as text about to arrive, which is what is actually coming.
 */
const COL_WIDTHS = ['w-24', 'w-32', 'w-20', 'w-28', 'w-16', 'w-24', 'w-20', 'w-28', 'w-16'];

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      <div className="flex gap-4 border-b border-border pb-3">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className={cn('h-3', COL_WIDTHS[i % COL_WIDTHS.length])} />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 py-1">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton
              key={c}
              className={cn('h-4', COL_WIDTHS[(c + r) % COL_WIDTHS.length])}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function CardsSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-28 w-full rounded" />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
}: {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-16 text-center',
        className,
      )}
    >
      {/* The can's ring again — an empty state is an empty "o". */}
      <div className="flex h-14 w-14 items-center justify-center rounded-full border-[3px] border-primary/70 bg-primary/10 text-foreground shadow-[0_0_28px_-6px_hsl(var(--primary))] transition-colors duration-500">
        {icon ?? <Inbox className="h-5 w-5" />}
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium">{title}</p>
        {description ? (
          <p className="mx-auto max-w-md text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export function ErrorState({
  onRetry,
  title,
  description,
  className,
}: {
  onRetry?: () => void;
  title?: string;
  description?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 rounded border border-red-200 bg-red-50 p-4',
        className,
      )}
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <p className="text-sm font-medium text-red-800">{title ?? t('common.error.title')}</p>
          <p className="mt-0.5 text-sm text-red-700">
            {description ?? t('common.error.description')}
          </p>
        </div>
        {onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw className="h-3.5 w-3.5" />
            {t('common.error.retry')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The screen-level state switch. Keeps every screen's happy path free of the three-way
 * `isLoading / isError / empty` ladder, so the ladder cannot be forgotten on one of them.
 */
export function QueryStates({
  isLoading,
  isError,
  isEmpty,
  onRetry,
  skeleton,
  empty,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  onRetry?: () => void;
  skeleton?: ReactNode;
  empty: ReactNode;
  children: ReactNode;
}) {
  if (isLoading) return <>{skeleton ?? <TableSkeleton />}</>;
  if (isError) return <ErrorState onRetry={onRetry} />;
  if (isEmpty) return <>{empty}</>;
  return <>{children}</>;
}
